import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import {
  buildDirectoryPageData,
  directoryConfig,
  directoryPageId,
  hydrateDirectoryPage,
  sortDirectoryItems,
} from "./commonDirectory";

const API_URL =
  "https://asia-east1-chat3test-59045.cloudfunctions.net/createCompanyAccount";

const TABS = [
  { key: "contacts", label: "常用聯絡人" },
  { key: "addresses", label: "常用地址" },
];

const FIELD_ALIASES = {
  name: ["name", "收件人姓名", "姓名", "聯絡人"],
  company: ["company", "公司", "公司名稱", "公司或店家名稱"],
  phone: ["phone", "聯絡電話 / 手機", "聯絡電話", "電話", "手機"],
  email: ["email", "Email", "E-mail", "電子郵件", "電子信箱", "信箱"],
  address: ["address", "地址", "詳細地址", "收件地址", "配送地址"],
};

const styles = {
  page: {
    width: "auto",
    height: "100vh",
    minHeight: 0,
    margin: -22,
    boxSizing: "border-box",
    padding: 22,
    overflowY: "auto",
    overflowX: "hidden",
    background: "#FFFFFF",
    color: "#111827",
    fontFamily:
      'Inter, "Noto Sans TC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 14,
    marginBottom: 16,
  },
  title: {
    margin: 0,
    color: "#111827",
    fontSize: 25,
    lineHeight: 1.12,
    letterSpacing: "-0.4px",
    fontWeight: 900,
  },
  desc: { margin: "8px 0 0", color: "#87919A", fontSize: 12, lineHeight: 1.7 },
  tabs: {
    display: "flex",
    width: "fit-content",
    maxWidth: "100%",
    gap: 3,
    overflowX: "auto",
    marginBottom: 0,
    padding: 3,
    border: "1px solid rgba(148,163,184,0.14)",
    borderRadius: 999,
    background: "#F8FAFC",
  },
  tab: {
    minWidth: 132,
    padding: "9px 16px",
    border: "1px solid transparent",
    borderRadius: 999,
    background: "transparent",
    color: "#64748B",
    textAlign: "center",
    cursor: "pointer",
  },
  tabActive: {
    borderColor: "#FFFFFF",
    background: "#FFFFFF",
    color: "#111827",
    boxShadow: "0 3px 9px rgba(15,23,42,0.08)",
  },
  tabLabel: { display: "block", fontSize: 13, fontWeight: 800 },
  card: {
    border: "1px solid rgba(148,163,184,0.12)",
    borderRadius: 24,
    background: "#FFFFFF",
    boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
  },
  cardHeader: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: "18px 24px 16px",
    borderBottom: "1px solid #EEF2F7",
  },
  cardTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: "-0.02em",
  },
  cardHint: {
    margin: "6px 0 0",
    color: "#8A949C",
    fontSize: 11,
    lineHeight: 1.6,
  },
  content: { padding: "18px 24px 24px" },
  primaryButton: {
    minHeight: 40,
    padding: "0 16px",
    border: 0,
    borderRadius: 12,
    background: "#111827",
    color: "#FFF",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    minHeight: 38,
    padding: "0 14px",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 12,
    background: "#F8FAFC",
    color: "#475569",
    fontSize: 12,
    fontWeight: 750,
    cursor: "pointer",
  },
  dangerButton: {
    minHeight: 34,
    padding: "0 11px",
    border: "1px solid #F0D4D0",
    borderRadius: 9,
    background: "#FFF8F7",
    color: "#B65B50",
    fontSize: 11,
    fontWeight: 750,
    cursor: "pointer",
  },
  form: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    padding: 18,
    border: "1px solid rgba(148,163,184,0.12)",
    borderRadius: 18,
    background: "#F8FAFC",
    marginBottom: 20,
  },
  full: { gridColumn: "1 / -1" },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { color: "#64748B", fontSize: 12, fontWeight: 800 },
  input: {
    width: "100%",
    minHeight: 42,
    boxSizing: "border-box",
    padding: "0 12px",
    border: "1px solid #E5E7EB",
    borderRadius: 11,
    outline: "none",
    background: "#FFF",
    color: "#26323B",
    fontSize: 13,
  },
  formActions: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    paddingTop: 4,
  },
  tableWrap: {
    overflowX: "auto",
    overflowY: "hidden",
    border: "1px solid rgba(148,163,184,0.12)",
    borderRadius: 16,
    background: "#FFFFFF",
  },
  table: {
    width: "100%",
    minWidth: 650,
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    padding: "12px 13px",
    borderBottom: "1px solid #EEF2F7",
    background: "#F8FAFC",
    color: "#64748B",
    textAlign: "left",
    fontSize: 10,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 13px",
    borderBottom: "1px solid #EEF2F7",
    color: "#34414A",
    verticalAlign: "top",
  },
  muted: { color: "#9AA4AB", fontSize: 11 },
  status: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 8px",
    borderRadius: 999,
    background: "#EFF8F2",
    color: "#39815B",
    fontSize: 10,
    fontWeight: 800,
  },
  notice: {
    marginBottom: 16,
    padding: "11px 13px",
    border: "1px solid #F0E1D4",
    borderRadius: 12,
    background: "#FFF9F3",
    color: "#9B652F",
    fontSize: 12,
    lineHeight: 1.6,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100,
    display: "grid",
    placeItems: "center",
    padding: 20,
    background: "rgba(15,23,42,.38)",
  },
  modal: {
    width: "min(680px, 100%)",
    maxHeight: "min(620px, calc(100vh - 40px))",
    overflowY: "auto",
    border: "1px solid #E7EBEE",
    borderRadius: 18,
    background: "#FFF",
    boxShadow: "0 24px 70px rgba(15,23,42,.22)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    padding: "22px 24px 15px",
    borderBottom: "1px solid #EEF1F3",
  },
  modalClose: {
    width: 32,
    height: 32,
    border: "1px solid #E1E6E9",
    borderRadius: 9,
    background: "#FFF",
    color: "#71808A",
    fontSize: 19,
    lineHeight: 1,
    cursor: "pointer",
  },
  importModal: {
    width: "min(980px, calc(100vw - 40px))",
    maxHeight: "calc(100vh - 40px)",
    overflowY: "auto",
    border: "1px solid #E7EBEE",
    borderRadius: 18,
    background: "#FFF",
    boxShadow: "0 24px 70px rgba(15,23,42,.22)",
  },
  importSummary: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    margin: "16px 0",
  },
  importMetric: {
    padding: "12px 14px",
    border: "1px solid #E9EDF1",
    borderRadius: 12,
    background: "#FBFCFD",
  },
  importActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
  importProgress: {
    marginTop: 16,
    padding: "14px 16px",
    border: "1px solid #D8EFE1",
    borderRadius: 13,
    background: "#F4FBF6",
  },
  importProgressTrack: {
    height: 8,
    marginTop: 10,
    overflow: "hidden",
    borderRadius: 999,
    background: "#DDEFE4",
  },
};

function clean(value) {
  return String(value ?? "").trim();
}

function directoryDuplicateKey(type, item) {
  const values =
    type === "contacts"
      ? [item?.name, item?.company, item?.phone, item?.email, item?.address]
      : [item?.address];
  return JSON.stringify(values.map((value) => clean(value)));
}

function textAddress(value) {
  return typeof value === "string" ? clean(value) : "";
}

function makeDirectoryItemId(type) {
  const prefix = type === "contacts" ? "contact" : "address";
  const random = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${random}`;
}

function formatTime(value) {
  if (!value) return "—";
  const date =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}`;
}

function parseCsv(source) {
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  const value = source.replace(/^\uFEFF/, "");

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    const next = value[index + 1];
    if (char === '"' && quoted && next === '"') {
      cell += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell);
      if (row.some((item) => clean(item))) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  row.push(cell);
  if (row.some((item) => clean(item))) rows.push(row);
  return rows;
}

function rowsFromFile(file, source) {
  if (file.name.toLowerCase().endsWith(".csv")) return parseCsv(source);
  const workbook = XLSX.read(source, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
}

function compactHeader(value) {
  return clean(value)
    .replace(/[（(].*?[）)]/g, "")
    .replace(/\s+/g, "");
}

function findField(headers, aliases) {
  const compacted = headers.map(compactHeader);
  const alias = aliases
    .map(compactHeader)
    .find((item) => compacted.includes(item));
  return alias ? clean(headers[compacted.indexOf(alias)]) : "";
}

function mapImportedRows(rawRows, tab = "contacts") {
  if (!rawRows.length) return { headers: [], rows: [] };
  const headers = rawRows[0].map(
    (header, index) => clean(header) || `欄位${index + 1}`,
  );
  const rows = rawRows.slice(1).map((cells, index) => {
    const raw = headers.reduce((result, header, cellIndex) => {
      result[header] = clean(cells[cellIndex]);
      return result;
    }, {});
    const read = (field) => {
      const header = findField(headers, FIELD_ALIASES[field]);
      return header ? clean(raw[header]) : "";
    };
    const name = read("name");
    const company = read("company");
    const phone = read("phone");
    const email = read("email");
    const address =
      read("address") || (headers.length === 1 ? clean(cells[0]) : "");
    const errors = [];
    if (tab === "contacts" && !name) errors.push("缺少收件人姓名");
    if (!address) errors.push("缺少地址");
    return {
      rowNumber: index + 2,
      name,
      company,
      phone,
      email,
      address,
      status: errors.length ? "error" : "valid",
      errors,
      raw,
    };
  });
  return { headers, rows };
}

function Field({ label, children, full = false }) {
  return (
    <label style={{ ...styles.field, ...(full ? styles.full : {}) }}>
      <span style={styles.label}>{label}</span>
      {children}
    </label>
  );
}

function ImportFileButton({ onChange, label = "匯入 Excel / CSV" }) {
  return (
    <label style={styles.secondaryButton}>
      {label}
      <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={onChange} />
    </label>
  );
}

function ImportPreview({
  file,
  rows,
  stats,
  status,
  loading,
  progress,
  checked,
  tab,
  onChangeFile,
  onCancel,
  onCheck,
  onConfirm,
}) {
  const columns =
    tab === "contacts"
      ? [
          ["name", "姓名"],
          ["company", "公司"],
          ["phone", "電話"],
          ["email", "Email"],
          ["address", "地址"],
        ]
      : [["address", "地址"]];
  const done = progress?.phase === "done";
  const total = Number(progress?.total) || stats.valid;
  const processed = Math.min(Number(progress?.processed) || 0, total);
  const percent = total ? Math.round((processed / total) * 100) : 0;
  const progressLabel =
    progress?.phase === "geocoding"
      ? "正在確認地址與定位資料…"
      : progress?.phase === "writing"
        ? "正在寫入常用資料…"
        : done
          ? "匯入完成"
          : "等待開始";

  return (
    <div
      style={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="檢查匯入資料"
    >
      <style>{`.common-import-spinner{display:inline-block;width:13px;height:13px;border:2px solid #B9EBD1;border-top-color:#1FA968;border-radius:50%;animation:common-import-spin .75s linear infinite}@keyframes common-import-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={styles.importModal}>
        <div style={styles.modalHeader}>
          <div>
            <p style={{ ...styles.cardHint, margin: 0 }}>資料匯入</p>
            <h2 style={{ ...styles.cardTitle, marginTop: 5 }}>檢查匯入資料</h2>
            <p style={styles.cardHint}>
              {tab === "contacts"
                ? "確認聯絡人資料後，再執行真正匯入。"
                : "確認地址資料後，再執行真正匯入。"}
            </p>
          </div>
          <button
            type="button"
            style={styles.modalClose}
            onClick={onCancel}
            disabled={loading}
            aria-label="關閉"
          >
            ×
          </button>
        </div>
        <div style={styles.content}>
          <div style={{ ...styles.notice, marginBottom: 0 }}>
            <strong>{file?.name || "未選擇檔案"}</strong>
          </div>
          <div style={styles.importSummary}>
            <div style={styles.importMetric}>
              <strong style={{ fontSize: 21 }}>{stats.total}</strong>
              <div style={styles.muted}>總列數</div>
            </div>
            <div style={styles.importMetric}>
              <strong style={{ fontSize: 21, color: "#2F8A5A" }}>
                {stats.valid}
              </strong>
              <div style={styles.muted}>可匯入</div>
            </div>
            <div style={styles.importMetric}>
              <strong
                style={{
                  fontSize: 21,
                  color: stats.errors ? "#B55240" : "#17212B",
                }}
              >
                {stats.errors}
              </strong>
              <div style={styles.muted}>需要修正</div>
            </div>
          </div>
          {progress ? (
            <div style={styles.importProgress}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  {loading && (
                    <span
                      className="common-import-spinner"
                      aria-hidden="true"
                    />
                  )}
                  {done && (
                    <span
                      style={{ color: "#1F9E62", fontSize: 18, lineHeight: 1 }}
                    >
                      ✓
                    </span>
                  )}
                  <strong style={{ color: "#28794F", fontSize: 13 }}>
                    {progressLabel}
                  </strong>
                </div>
                <strong style={{ color: "#1F9E62", fontSize: 14 }}>
                  {processed}/{total}
                </strong>
              </div>
              <div style={styles.importProgressTrack}>
                <div
                  style={{
                    width: `${percent}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: "linear-gradient(90deg, #50C98A, #1FAD68)",
                    transition: "width .45s ease",
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 7,
                  color: "#7F9187",
                  fontSize: 11,
                }}
              >
                <span>{status || "請稍候…"}</span>
                <span>{percent}%</span>
              </div>
            </div>
          ) : null}
          <div
            style={{
              ...styles.tableWrap,
              maxHeight: "min(46vh, 440px)",
              overflowY: "auto",
              marginTop: 16,
            }}
          >
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>列</th>
                  <th style={styles.th}>狀態</th>
                  {columns.map(([, label]) => (
                    <th key={label} style={styles.th}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 100).map((row) => (
                  <tr key={row.rowNumber}>
                    <td style={styles.td}>{row.rowNumber}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.status,
                          ...(row.status === "error"
                            ? { background: "#FFF2EF", color: "#B55240" }
                            : {}),
                        }}
                      >
                        {row.status === "error" ? "需修正" : "可匯入"}
                      </span>
                      {row.errors.length > 0 && (
                        <div
                          style={{
                            ...styles.muted,
                            color: "#B55240",
                            marginTop: 4,
                          }}
                        >
                          {row.errors.join("、")}
                        </div>
                      )}
                    </td>
                    {columns.map(([key]) => (
                      <td key={`${row.rowNumber}-${key}`} style={styles.td}>
                        {row[key] || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 100 && (
            <p style={styles.muted}>
              預覽只顯示前 100 列，實際會處理全部 {rows.length} 列。
            </p>
          )}
          <div style={styles.importActions}>
            <ImportFileButton onChange={onChangeFile} label="重新選擇檔案" />
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={onCancel}
              disabled={loading}
            >
              取消
            </button>
            <button
              type="button"
              style={styles.primaryButton}
              disabled={loading || (checked && !stats.valid && !done)}
              onClick={done ? onCancel : checked ? onConfirm : onCheck}
            >
              {done
                ? "完成"
                : loading
                  ? "匯入中…"
                  : checked
                    ? `開始匯入 ${stats.valid} 筆`
                    : "檢查資料"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditModal({
  target,
  contactForm,
  setContactForm,
  addressForm,
  setAddressForm,
  onClose,
  onSubmit,
}) {
  if (!target) return null;
  const isContact = target.type === "contact";
  const form = isContact ? contactForm : addressForm;
  const setForm = isContact ? setContactForm : setAddressForm;
  return (
    <div style={styles.modalOverlay} role="presentation" onMouseDown={onClose}>
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div>
            <h2 style={{ ...styles.cardTitle, marginTop: 7 }}>
              {isContact ? "編輯常用聯絡人" : "編輯常用地址"}
            </h2>
            <p style={styles.cardHint}>地址修改後會重新查詢郵遞區號與座標。</p>
          </div>
          <button
            type="button"
            style={styles.modalClose}
            onClick={onClose}
            aria-label="關閉"
          >
            ×
          </button>
        </div>
        <form
          style={{
            ...styles.content,
            ...styles.form,
            margin: 0,
            border: 0,
            borderRadius: 0,
            background: "#FFF",
          }}
          onSubmit={onSubmit}
        >
          {isContact && (
            <>
              <Field label="聯絡人姓名 *">
                <input
                  style={styles.input}
                  value={form.name}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                />
              </Field>
              <Field label="公司或店家名稱">
                <input
                  style={styles.input}
                  value={form.company}
                  onChange={(event) =>
                    setForm({ ...form, company: event.target.value })
                  }
                />
              </Field>
              <Field label="電話">
                <input
                  style={styles.input}
                  value={form.phone}
                  onChange={(event) =>
                    setForm({ ...form, phone: event.target.value })
                  }
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  style={styles.input}
                  value={form.email}
                  onChange={(event) =>
                    setForm({ ...form, email: event.target.value })
                  }
                  placeholder="選填，例如：name@example.com"
                />
              </Field>
            </>
          )}
          <Field label="地址 *" full>
            <input
              style={styles.input}
              value={form.address}
              onChange={(event) =>
                setForm({ ...form, address: event.target.value })
              }
              placeholder="例如：高雄市三民區建國三路123號4樓"
            />
          </Field>
          <div style={styles.formActions}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={onClose}
            >
              取消
            </button>
            <button type="submit" style={styles.primaryButton}>
              儲存修改
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DriverManagerCommonInfo({ user, account }) {
  const [authUser, setAuthUser] = useState(user || auth.currentUser || null);
  const [resolvedAccount, setResolvedAccount] = useState(account || null);
  const [accountLoading, setAccountLoading] = useState(!account?.orgId);
  const uid = clean(user?.uid || authUser?.uid || auth.currentUser?.uid);
  const orgId = clean(account?.orgId || resolvedAccount?.orgId);
  const [activeTab, setActiveTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [dedupePreview, setDedupePreview] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    address: "",
  });
  const [addressForm, setAddressForm] = useState({ address: "" });
  const [importFile, setImportFile] = useState(null);
  const [importRows, setImportRows] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importStatus, setImportStatus] = useState("");
  const [importProgress, setImportProgress] = useState(null);
  const [importChecked, setImportChecked] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [directoryLoading, setDirectoryLoading] = useState({
    contacts: false,
    addresses: false,
  });
  const [directoryHasMore, setDirectoryHasMore] = useState({
    contacts: true,
    addresses: true,
  });
  const [directoryVisibleCount, setDirectoryVisibleCount] = useState({
    contacts: 120,
    addresses: 120,
  });
  const directoryPagesRef = useRef({ contacts: [], addresses: [] });
  const directoryLoadingRef = useRef({ contacts: false, addresses: false });
  const geoCacheRef = useRef(new Map());
  const geoCacheLoadedRef = useRef(false);

  useEffect(() => {
    if (user) {
      setAuthUser(user);
      return undefined;
    }
    return onAuthStateChanged(auth, (firebaseUser) =>
      setAuthUser(firebaseUser || null),
    );
  }, [user]);

  useEffect(() => {
    if (account?.orgId) {
      setResolvedAccount(account);
      setAccountLoading(false);
      return undefined;
    }
    if (!uid) {
      setResolvedAccount(null);
      setAccountLoading(false);
      return undefined;
    }
    let active = true;
    setAccountLoading(true);
    getDoc(doc(db, "drivers", uid))
      .then((snapshot) => {
        if (active)
          setResolvedAccount(
            snapshot.exists() ? { uid, ...snapshot.data() } : null,
          );
      })
      .catch((loadError) => {
        console.error(
          "[DriverManagerCommonInfo] account load error:",
          loadError,
        );
        if (active) setResolvedAccount(null);
      })
      .finally(() => {
        if (active) setAccountLoading(false);
      });
    return () => {
      active = false;
    };
  }, [account, uid]);

  useEffect(() => {
    setContacts([]);
    setAddresses([]);
    directoryPagesRef.current = { contacts: [], addresses: [] };
    directoryLoadingRef.current = { contacts: false, addresses: false };
    geoCacheRef.current = new Map();
    geoCacheLoadedRef.current = false;
    setDirectoryLoading({ contacts: false, addresses: false });
    setDirectoryHasMore({ contacts: true, addresses: true });
    setDirectoryVisibleCount({ contacts: 120, addresses: 120 });
  }, [activeTab, orgId]);

  const loadDirectoryPage = async (type, requestedPage = null) => {
    if (!orgId || directoryLoadingRef.current[type]) return;
    const config = directoryConfig(type);
    const loadedPages = directoryPagesRef.current[type];
    const pageNo =
      requestedPage || (loadedPages.length ? Math.max(...loadedPages) + 1 : 1);
    if (loadedPages.includes(pageNo)) return;

    directoryLoadingRef.current[type] = true;
    setDirectoryLoading((current) => ({ ...current, [type]: true }));
    setError("");
    try {
      const snapshot = await getDoc(
        doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
      );
      const page = hydrateDirectoryPage(snapshot, pageNo);
      if (!page.exists) {
        setDirectoryHasMore((current) => ({ ...current, [type]: false }));
        if (!loadedPages.length) {
          if (type === "contacts") setContacts([]);
          else setAddresses([]);
        }
        return;
      }
      loadedPages.push(pageNo);
      const setItems = type === "contacts" ? setContacts : setAddresses;
      setItems((current) => sortDirectoryItems([...current, ...page.items]));
      setDirectoryHasMore((current) => ({
        ...current,
        [type]: page.items.length >= config.pageCapacity,
      }));
    } catch (loadError) {
      console.error(
        "[DriverManagerCommonInfo] directory page load error:",
        loadError,
      );
      setError(`${config.label}讀取失敗，請確認目前帳號權限後再試。`);
    } finally {
      directoryLoadingRef.current[type] = false;
      setDirectoryLoading((current) => ({ ...current, [type]: false }));
    }
  };

  const readLatestDirectoryPage = async (type) => {
    const config = directoryConfig(type);
    const metaSnapshot = await getDoc(
      doc(db, "orgs", orgId, config.collection, "directoryMeta"),
    );
    const metaPageNo = Math.max(
      1,
      Number(metaSnapshot.data()?.latestPage) || 1,
    );
    let pageNo = metaSnapshot.exists() ? metaPageNo : 1;
    let latestSnapshot = await getDoc(
      doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
    );
    if (!latestSnapshot.exists() && pageNo !== 1) {
      pageNo = 1;
      latestSnapshot = await getDoc(
        doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
      );
    }
    if (!latestSnapshot.exists())
      return { exists: false, pageNo: 1, items: [] };

    // 舊資料若尚未有 directoryMeta，只在頁面已滿時向後找，之後新增時會建立 metadata。
    while (!metaSnapshot.exists()) {
      const currentPage = hydrateDirectoryPage(latestSnapshot, pageNo);
      if (currentPage.items.length < config.pageCapacity) return currentPage;
      const nextPageNo = pageNo + 1;
      const nextSnapshot = await getDoc(
        doc(db, "orgs", orgId, config.collection, directoryPageId(nextPageNo)),
      );
      if (!nextSnapshot.exists()) return currentPage;
      pageNo = nextPageNo;
      latestSnapshot = nextSnapshot;
    }
    return hydrateDirectoryPage(latestSnapshot, pageNo);
  };

  const writeDirectoryPage = async (type, pageNo, items, isNewPage = false) => {
    const config = directoryConfig(type);
    const payload = buildDirectoryPageData({
      type,
      pageNo,
      items,
      uid,
      isNewPage,
      serverTimestampValue: serverTimestamp(),
    });
    await setDoc(
      doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
      payload,
    );
    const pageList = directoryPagesRef.current[type];
    if (!pageList.includes(pageNo)) pageList.push(pageNo);
    const setItems = type === "contacts" ? setContacts : setAddresses;
    setItems((current) => {
      const withoutPage = current.filter((item) => item.__pageNo !== pageNo);
      return sortDirectoryItems([
        ...withoutPage,
        ...items.map((item) => ({ ...item, __pageNo: pageNo })),
      ]);
    });
    setDirectoryHasMore((current) => ({
      ...current,
      [type]: items.length >= config.pageCapacity,
    }));
  };

  const appendDirectoryItems = async (type, newItems, onProgress = null) => {
    if (onProgress && newItems.length > 50) {
      let processed = 0;
      for (let start = 0; start < newItems.length; start += 50) {
        const chunk = newItems.slice(start, start + 50);
        await appendDirectoryItems(type, chunk);
        processed += chunk.length;
        onProgress(processed);
      }
      return;
    }
    const config = directoryConfig(type);
    const latest = await readLatestDirectoryPage(type);
    let pageNo = latest.pageNo;
    let pageItems = latest.items || [];
    let cursor = 0;
    const writes = [];
    while (cursor < newItems.length) {
      const available = Math.max(0, config.pageCapacity - pageItems.length);
      if (available === 0) {
        pageNo += 1;
        pageItems = [];
      }
      const take = Math.min(
        config.pageCapacity - pageItems.length,
        newItems.length - cursor,
      );
      const chunk = newItems
        .slice(cursor, cursor + take)
        .map((item) => ({ ...item, __pageNo: pageNo }));
      pageItems = [...pageItems, ...chunk];
      writes.push({
        pageNo,
        items: pageItems,
        isNewPage: !latest.exists || pageNo !== latest.pageNo,
      });
      cursor += take;
      if (cursor < newItems.length) {
        pageNo += 1;
        pageItems = [];
      }
    }
    const batch = writeBatch(db);
    writes.forEach(({ pageNo: targetPageNo, items, isNewPage }) => {
      batch.set(
        doc(
          db,
          "orgs",
          orgId,
          config.collection,
          directoryPageId(targetPageNo),
        ),
        buildDirectoryPageData({
          type,
          pageNo: targetPageNo,
          items,
          uid,
          isNewPage,
          serverTimestampValue: serverTimestamp(),
        }),
        { merge: true },
      );
    });
    const latestPageNo = Math.max(
      ...writes.map(({ pageNo: targetPageNo }) => targetPageNo),
    );
    batch.set(
      doc(db, "orgs", orgId, config.collection, "directoryMeta"),
      {
        schemaVersion: 1,
        latestPage: latestPageNo,
        updatedBy: uid || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    await batch.commit();
    const persistedItems = writes.flatMap(({ items }) => items);
    const writtenPages = new Set(
      writes.map(({ pageNo: targetPageNo }) => targetPageNo),
    );
    const setItems = type === "contacts" ? setContacts : setAddresses;
    setItems((current) =>
      sortDirectoryItems([
        ...current.filter((item) => !writtenPages.has(item.__pageNo)),
        ...persistedItems,
      ]),
    );
    writes.forEach(({ pageNo: targetPageNo }) => {
      if (!directoryPagesRef.current[type].includes(targetPageNo))
        directoryPagesRef.current[type].push(targetPageNo);
    });
  };

  const handleDirectoryWheel = (type, event) => {
    if (event.deltaY <= 0) return;
    const items = type === "contacts" ? contacts : addresses;
    const visibleCount = directoryVisibleCount[type];
    if (!directoryPagesRef.current[type].length) {
      loadDirectoryPage(type, 1);
      return;
    }
    if (visibleCount >= items.length - 20 && directoryHasMore[type]) {
      setDirectoryVisibleCount((current) => ({
        ...current,
        [type]: current[type] + 120,
      }));
      loadDirectoryPage(type);
    }
  };

  const handleDirectoryScroll = (type, event) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight < target.scrollHeight - 80)
      return;
    setDirectoryVisibleCount((current) => ({
      ...current,
      [type]: current[type] + 120,
    }));
    if (directoryHasMore[type]) loadDirectoryPage(type);
  };

  const importStats = useMemo(() => {
    const total = importRows.length;
    const errors = importRows.filter((row) => row.status === "error").length;
    return { total, valid: total - errors, errors };
  }, [importRows]);

  const resetForms = () => {
    setContactForm({
      name: "",
      company: "",
      phone: "",
      email: "",
      address: "",
    });
    setAddressForm({ address: "" });
  };

  const getIdToken = async () => {
    const currentUser = auth.currentUser || authUser;
    if (!currentUser) throw new Error("尚未登入 Firebase。");
    return currentUser.getIdToken();
  };

  const callHere = async (payload) => {
    const idToken = await getIdToken();
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ ...payload, orgId }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok)
      throw new Error(result.message || "地址查詢失敗。");
    return result;
  };

  const findCachedGeo = (address, currentItem = null) => {
    const target = clean(address);
    const cachedByAddress = geoCacheRef.current.get(target);
    if (cachedByAddress) return cachedByAddress;
    const items = [currentItem, ...contacts, ...addresses].filter(Boolean);
    return items.find(
      (item) =>
        textAddress(item.address) === target &&
        item.geocodeStatus === "verified" &&
        Number.isFinite(Number(item.lat)) &&
        Number.isFinite(Number(item.lng)),
    );
  };

  const ensureGeoCache = async () => {
    if (geoCacheLoadedRef.current || !orgId) return;
    for (const type of ["contacts", "addresses"]) {
      const config = directoryConfig(type);
      const metaSnapshot = await getDoc(
        doc(db, "orgs", orgId, config.collection, "directoryMeta"),
      );
      const latestPage = Math.max(
        1,
        Number(metaSnapshot.data()?.latestPage) || 1,
      );
      for (let pageNo = 1; pageNo <= latestPage; pageNo += 1) {
        const snapshot = await getDoc(
          doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
        );
        if (!snapshot.exists()) continue;
        hydrateDirectoryPage(snapshot, pageNo).items.forEach((item) => {
          const itemAddress = textAddress(item.address);
          if (
            itemAddress &&
            item.geocodeStatus === "verified" &&
            Number.isFinite(Number(item.lat)) &&
            Number.isFinite(Number(item.lng))
          ) {
            geoCacheRef.current.set(itemAddress, item);
          }
        });
      }
    }
    geoCacheLoadedRef.current = true;
  };

  const resolveGeo = async (address, currentItem = null) => {
    await ensureGeoCache();
    const cached = findCachedGeo(address, currentItem);
    if (cached) {
      return {
        address: targetAddress(address),
        postalCode: clean(cached.postalCode),
        lat: Number(cached.lat),
        lng: Number(cached.lng),
        geocodeStatus: "verified",
      };
    }
    const result = await callHere({
      action: "geocodeAddress",
      address: clean(address),
    });
    const resolved =
      result.results?.[0] || {
        address: clean(address),
        postalCode: "",
        lat: null,
        lng: null,
        geocodeStatus: "not_found",
      };
    if (
      resolved.geocodeStatus === "verified" &&
      Number.isFinite(Number(resolved.lat)) &&
      Number.isFinite(Number(resolved.lng))
    ) {
      geoCacheRef.current.set(clean(address), resolved);
    }
    return resolved;
  };

  const saveContact = async (event) => {
    event.preventDefault();
    const name = clean(contactForm.name);
    const address = clean(contactForm.address);
    if (!orgId || !name || !address) {
      setError("請填寫聯絡人姓名與地址。");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const geo = await resolveGeo(address);
      await appendDirectoryItems("contacts", [
        {
          id: makeDirectoryItemId("contacts"),
          name,
          company: clean(contactForm.company),
          phone: clean(contactForm.phone),
          email: clean(contactForm.email),
          address,
          postalCode: clean(geo.postalCode),
          lat: geo.lat,
          lng: geo.lng,
          geocodeStatus: geo.geocodeStatus,
          updatedAt: new Date(),
        },
      ]);
      resetForms();
      setMessage(
        geo.geocodeStatus === "verified"
          ? "常用聯絡人已新增，定位資料已保存。"
          : "聯絡人已新增，但找不到這個地址的位置。",
      );
    } catch (saveError) {
      console.error("[DriverManagerCommonInfo] save contact error:", saveError);
      setError(saveError.message || "常用聯絡人儲存失敗。");
    } finally {
      setSaving(false);
    }
  };

  const saveAddress = async (event) => {
    event.preventDefault();
    const address = clean(addressForm.address);
    if (!orgId || !address) {
      setError("請填寫地址。");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const geo = await resolveGeo(address);
      await appendDirectoryItems("addresses", [
        {
          id: makeDirectoryItemId("addresses"),
          address,
          postalCode: clean(geo.postalCode),
          lat: geo.lat,
          lng: geo.lng,
          geocodeStatus: geo.geocodeStatus,
          updatedAt: new Date(),
        },
      ]);
      resetForms();
      setMessage(
        geo.geocodeStatus === "verified"
          ? "常用地址已新增，定位資料已保存。"
          : "地址已新增，但找不到這個地址的位置。",
      );
    } catch (saveError) {
      console.error("[DriverManagerCommonInfo] save address error:", saveError);
      setError(saveError.message || "常用地址儲存失敗。");
    } finally {
      setSaving(false);
    }
  };

  const editContact = (item) => {
    setEditTarget({ type: "contact", id: item.id });
    setContactForm({
      name: clean(item.name),
      company: clean(item.company),
      phone: clean(item.phone),
      email: clean(item.email),
      address: textAddress(item.address),
    });
    setError("");
    setMessage("");
  };

  const editAddress = (item) => {
    setEditTarget({ type: "address", id: item.id });
    setAddressForm({ address: textAddress(item.address) });
    setError("");
    setMessage("");
  };

  const removeDuplicateDirectoryItems = async (type, execute = false) => {
    const label = type === "contacts" ? "聯絡人" : "地址";
    if (dedupeLoading) return;
    setDedupeLoading(true);
    setError("");
    setMessage("");
    try {
      const config = directoryConfig(type);
      const metaSnapshot = await getDoc(doc(db, "orgs", orgId, config.collection, "directoryMeta"));
      const latestPage = Math.max(1, Number(metaSnapshot.data()?.latestPage) || 1);
      const pages = [];
      for (let pageNo = 1; pageNo <= latestPage; pageNo += 1) {
        const snapshot = await getDoc(doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)));
        if (snapshot.exists()) pages.push({ pageNo, ...hydrateDirectoryPage(snapshot, pageNo) });
      }
      const seen = new Set();
      const duplicateIds = new Set();
      pages.forEach((page) => page.items.forEach((item) => {
        const key = directoryDuplicateKey(type, item);
        if (seen.has(key)) duplicateIds.add(item.id);
        else seen.add(key);
      }));
      if (!duplicateIds.size) {
        setMessage(`沒有找到內容完全相同的重複${label}。`);
        return;
      }
      if (!execute) {
        setDedupePreview({
          type,
          label,
          items: pages
            .flatMap((page) => page.items)
            .filter((item) => duplicateIds.has(item.id)),
        });
        return;
      }
      for (const page of pages) {
        const nextItems = page.items.filter((item) => !duplicateIds.has(item.id));
        if (nextItems.length !== page.items.length) await writeDirectoryPage(type, page.pageNo, nextItems);
      }
      setMessage(`已清理 ${duplicateIds.size} 筆重複${label}，每組保留最早的一筆。`);
    } catch (dedupeError) {
      console.error("[DriverManagerCommonInfo] dedupe error:", dedupeError);
      setError(`重複${label}清理失敗，請稍後再試。`);
    } finally {
      setDedupeLoading(false);
    }
  };
  const deleteItem = async (collectionName, item) => {
    const label = collectionName === "contacts" ? "聯絡人" : "地址";
    if (
      !window.confirm(
        `確定要刪除這筆${label}嗎？刪除後資料庫中的資料也會一併移除。`,
      )
    )
      return;
    try {
      const pageNo = Number(item.__pageNo) || 1;
      const config = directoryConfig(collectionName);
      const snapshot = await getDoc(
        doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
      );
      const page = hydrateDirectoryPage(snapshot, pageNo);
      if (!page.exists) throw new Error("找不到這筆常用資料所在的分頁。");
      await writeDirectoryPage(
        collectionName,
        pageNo,
        page.items.filter((entry) => entry.id !== item.id),
      );
      setMessage(`${label}已從資料庫中刪除。`);
    } catch (deleteError) {
      console.error("[DriverManagerCommonInfo] delete error:", deleteError);
      setError(`${label}刪除失敗。`);
    }
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editTarget) return;
    const isContact = editTarget.type === "contact";
    const form = isContact ? contactForm : addressForm;
    const address = clean(form.address);
    if (!address || (isContact && !clean(form.name))) {
      setError(isContact ? "請填寫聯絡人姓名與地址。" : "請填寫地址。");
      return;
    }
    const currentItems = isContact ? contacts : addresses;
    const currentItem = currentItems.find((item) => item.id === editTarget.id);
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const geo = await resolveGeo(address, currentItem);
      const pageNo = Number(currentItem?.__pageNo) || 1;
      const collectionName = isContact ? "contacts" : "addresses";
      const config = directoryConfig(collectionName);
      const pageSnapshot = await getDoc(
        doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
      );
      const page = hydrateDirectoryPage(pageSnapshot, pageNo);
      if (!page.exists) throw new Error("找不到這筆常用資料所在的分頁。");
      const nextItem = isContact
        ? {
            ...currentItem,
            id: editTarget.id,
            name: clean(form.name),
            company: clean(form.company),
            phone: clean(form.phone),
            email: clean(form.email),
            address,
            postalCode: clean(geo.postalCode),
            lat: geo.lat,
            lng: geo.lng,
            geocodeStatus: geo.geocodeStatus,
            updatedAt: new Date(),
          }
        : {
            ...currentItem,
            id: editTarget.id,
            address,
            postalCode: clean(geo.postalCode),
            lat: geo.lat,
            lng: geo.lng,
            geocodeStatus: geo.geocodeStatus,
            updatedAt: new Date(),
          };
      await writeDirectoryPage(
        collectionName,
        pageNo,
        page.items.map((item) => (item.id === editTarget.id ? nextItem : item)),
      );
      setMessage(isContact ? "常用聯絡人已更新。" : "常用地址已更新。");
      setEditTarget(null);
      resetForms();
    } catch (saveError) {
      console.error("[DriverManagerCommonInfo] edit save error:", saveError);
      setError(saveError.message || "編輯內容儲存失敗。");
    } finally {
      setSaving(false);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportStatus("");
    setImportProgress(null);
    setImportChecked(false);
    setError("");
    try {
      const extension = file.name.toLowerCase().split(".").pop();
      const source =
        extension === "csv" ? await file.text() : await file.arrayBuffer();
      const mapped = mapImportedRows(rowsFromFile(file, source), activeTab);
      setImportFile(file);
      setImportRows(mapped.rows);
      setImportOpen(true);
      setImportStatus(`已讀取 ${mapped.rows.length} 筆，請先檢查資料。`);
    } catch (fileError) {
      console.error("[DriverManagerCommonInfo] import parse error:", fileError);
      setError("檔案解析失敗，請確認是有效的 Excel 或 CSV 檔案。");
      setImportFile(null);
      setImportRows([]);
    } finally {
      setImportLoading(false);
      event.target.value = "";
    }
  };

  const confirmImport = async () => {
    if (!orgId || !importFile || !importRows.length) return;
    const validRows = importRows.filter((row) => row.status === "valid");
    if (!validRows.length) return;
    setImportLoading(true);
    setImportChecked(true);
    setImportProgress({
      phase: "geocoding",
      processed: 0,
      total: validRows.length,
    });
    setError("");
    setImportStatus(`正在確認 ${validRows.length} 筆地址…`);
    try {
      const uniqueAddresses = [
        ...new Set(validRows.map((row) => clean(row.address))),
      ];
      await ensureGeoCache();
      const geoMap = new Map();
      const missingAddresses = [];
      uniqueAddresses.forEach((address) => {
        const cached = findCachedGeo(address);
        if (cached) geoMap.set(address, cached);
        else missingAddresses.push(address);
      });
      if (missingAddresses.length) {
        const geoResponse = await callHere({
          action: "geocodeAddress",
          addresses: missingAddresses,
        });
        (geoResponse.results || []).forEach((item) => {
          geoMap.set(clean(item.address), item);
          if (
            item.geocodeStatus === "verified" &&
            Number.isFinite(Number(item.lat)) &&
            Number.isFinite(Number(item.lng))
          ) {
            geoCacheRef.current.set(clean(item.address), item);
          }
        });
      }
      const readyRows = validRows.filter((row) =>
        geoMap.has(clean(row.address)),
      );
      const missingRows = validRows.filter(
        (row) => !geoMap.has(clean(row.address)),
      );
      if (missingRows.length) {
        setImportRows(
          importRows.map((row) =>
            missingRows.some((missing) => missing.rowNumber === row.rowNumber)
              ? { ...row, status: "error", errors: ["地址查詢失敗"] }
              : row,
          ),
        );
      }
      if (!readyRows.length) {
        setImportProgress({
          phase: "error",
          processed: 0,
          total: validRows.length,
        });
        setImportStatus("沒有可匯入的資料，請先修正錯誤列。");
        return;
      }
      setImportProgress({
        phase: "writing",
        processed: 0,
        total: readyRows.length,
      });
      setImportStatus(`地址確認完成，準備寫入 ${readyRows.length} 筆…`);
      const importItems = readyRows.map((row) => {
        const geo = geoMap.get(clean(row.address));
        return activeTab === "contacts"
          ? {
              id: makeDirectoryItemId("contacts"),
              name: row.name,
              company: row.company,
              phone: row.phone,
              email: row.email,
              address: clean(row.address),
              postalCode: clean(geo.postalCode),
              lat: geo.lat,
              lng: geo.lng,
              geocodeStatus: geo.geocodeStatus,
              updatedAt: new Date(),
            }
          : {
              id: makeDirectoryItemId("addresses"),
              address: clean(row.address),
              postalCode: clean(geo.postalCode),
              lat: geo.lat,
              lng: geo.lng,
              geocodeStatus: geo.geocodeStatus,
              updatedAt: new Date(),
            };
      });
      await appendDirectoryItems(activeTab, importItems, (processed) => {
        setImportProgress({
          phase: "writing",
          processed,
          total: readyRows.length,
        });
        setImportStatus(`正在寫入 ${processed}/${readyRows.length} 筆…`);
      });
      setImportProgress({
        phase: "done",
        processed: readyRows.length,
        total: readyRows.length,
      });
      setImportStatus(
        `已匯入 ${readyRows.length} 筆${activeTab === "contacts" ? "常用聯絡人" : "常用地址"}。${missingRows.length ? `另有 ${missingRows.length} 筆地址查詢失敗，未寫入。` : ""}`,
      );
      setMessage("匯入完成。 ");
    } catch (importError) {
      console.error(
        "[DriverManagerCommonInfo] import write error:",
        importError,
      );
      setError(importError.message || "匯入失敗，請稍後再試。");
      setImportProgress({
        phase: "error",
        processed: 0,
        total: validRows.length,
      });
      setImportStatus("匯入失敗，資料尚未完成寫入。");
    } finally {
      setImportLoading(false);
    }
  };

  if (accountLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.content}>
            <strong>正在準備常用資訊…</strong>
            <p style={styles.desc}>請稍候。</p>
          </div>
        </div>
      </div>
    );
  }

  if (!orgId) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.content}>
            <strong>目前無法使用常用資訊</strong>
            <p style={styles.desc}>請重新登入或確認帳號權限後再試。</p>
          </div>
        </div>
      </div>
    );
  }

  const visibleContacts = contacts.slice(0, directoryVisibleCount.contacts);
  const visibleAddresses = addresses.slice(0, directoryVisibleCount.addresses);
  const contactRows = visibleContacts.map((item, index) => (
    <tr
      key={item.id}
      className="common-directory-row"
      style={{ animationDelay: `${Math.min(index, 10) * 32}ms` }}
    >
      <td style={styles.td}>
        <strong>{item.name || "未命名"}</strong>
        <div style={styles.muted}>{item.company || ""}</div>
        <div style={styles.muted}>{formatTime(item.updatedAt)}</div>
      </td>
      <td style={styles.td}>{item.phone || "—"}</td>
      <td style={styles.td}>{item.email || "—"}</td>
      <td style={styles.td}>
        {textAddress(item.address) || "—"}
        <div style={styles.muted}>{item.postalCode || "尚未取得郵遞區號"}</div>
      </td>
      <td style={styles.td}>
        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() => editContact(item)}
        >
          編輯
        </button>{" "}
        <button
          type="button"
          style={styles.dangerButton}
          onClick={() => deleteItem("contacts", item)}
        >
          刪除
        </button>
      </td>
    </tr>
  ));

  const addressRows = visibleAddresses.map((item, index) => (
    <tr
      key={item.id}
      className="common-directory-row"
      style={{ animationDelay: `${Math.min(index, 10) * 32}ms` }}
    >
      <td style={styles.td}>
        <strong>{textAddress(item.address) || "未命名地址"}</strong>
        <div style={styles.muted}>{item.postalCode || "尚未取得郵遞區號"}</div>
      </td>
      <td style={styles.td}>
        {Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lng))
          ? `${item.lat}, ${item.lng}`
          : "尚未取得"}
      </td>
      <td style={styles.td}>
        <span
          style={{
            ...styles.status,
            ...(item.geocodeStatus !== "verified"
              ? { background: "#FFF8E8", color: "#A87523" }
              : {}),
          }}
        >
          {item.geocodeStatus || "pending"}
        </span>
      </td>
      <td style={styles.td}>
        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() => editAddress(item)}
        >
          編輯
        </button>{" "}
        <button
          type="button"
          style={styles.dangerButton}
          onClick={() => deleteItem("addresses", item)}
        >
          刪除
        </button>
      </td>
    </tr>
  ));

  return (
    <div
      style={styles.page}
      onWheel={(event) => {
        if (event.deltaY > 0) loadDirectoryPage(activeTab, 1);
      }}
      onScroll={(event) => {
        const target = event.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 120) {
          loadDirectoryPage(activeTab, 1);
        }
      }}
    >
      <style>{`@keyframes common-directory-reveal{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}.common-directory-row{animation:common-directory-reveal .42s cubic-bezier(.22,.61,.36,1) both}.common-directory-row td{transition:background-color .16s ease}.common-directory-row:hover td{background:#FFF9F2}@media (prefers-reduced-motion:reduce){.common-directory-row{animation:none!important;transition:none!important}}`}</style>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>常用資訊</h1>
        </div>
        <div
          style={{ ...styles.tabs, marginBottom: 0 }}
          role="tablist"
          aria-label="常用資訊分類"
        >
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              style={{
                ...styles.tab,
                ...(activeTab === tab.key ? styles.tabActive : {}),
              }}
              onClick={() => {
                setActiveTab(tab.key);
                setImportOpen(false);
                setError("");
                setMessage("");
              }}
            >
              <span style={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          style={{
            ...styles.notice,
            borderColor: "#F0D2CE",
            background: "#FFF5F3",
            color: "#A64F46",
          }}
        >
          {error}
        </div>
      )}
      {message && (
        <div
          style={{
            ...styles.notice,
            borderColor: "#D5E9DA",
            background: "#F2FBF4",
            color: "#397755",
          }}
        >
          {message}
        </div>
      )}
      {dedupePreview && (
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "rgba(15, 23, 42, 0.42)",
          }}
          onClick={() => {
            if (!dedupeLoading) setDedupePreview(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="重複資訊確認"
            style={{
              width: "min(680px, 100%)",
              maxHeight: "min(680px, 90vh)",
              display: "flex",
              flexDirection: "column",
              padding: 24,
              borderRadius: 18,
              background: "#FFFFFF",
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <strong style={{ fontSize: 18, color: "#17212B" }}>
              找到 {dedupePreview.items.length} 筆重複{dedupePreview.label}
            </strong>
            <div
              style={{
                marginTop: 16,
                maxHeight: 390,
                overflowY: "auto",
                border: "1px solid #E7EBEF",
                borderRadius: 12,
                padding: "4px 14px",
              }}
            >
              {dedupePreview.items.map((item) => (
                <div
                  key={`${item.__pageNo}-${item.id}`}
                  style={{ padding: "10px 0", borderBottom: "1px solid #E7EBEF", fontSize: 13, lineHeight: 1.6 }}
                >
                  {dedupePreview.type === "contacts"
                    ? `${clean(item.name)}｜${clean(item.company)}｜${clean(item.phone)}｜${clean(item.email)}｜${textAddress(item.address)}`
                    : textAddress(item.address)}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={dedupeLoading}
                onClick={() => setDedupePreview(null)}
              >
                取消
              </button>
              <button
                type="button"
                style={styles.primaryButton}
                disabled={dedupeLoading}
                onClick={async () => {
                  const preview = dedupePreview;
                  await removeDuplicateDirectoryItems(preview.type, true);
                  setDedupePreview(null);
                }}
              >
                {dedupeLoading ? "刪除中…" : "確認刪除"}
              </button>
            </div>
          </div>
        </div>
      )}
      {importOpen && (
        <ImportPreview
          file={importFile}
          rows={importRows}
          stats={importStats}
          status={importStatus}
          loading={importLoading}
          progress={importProgress}
          checked={importChecked}
          tab={activeTab}
          onChangeFile={handleImportFile}
          onCancel={() => {
            setImportOpen(false);
            setImportStatus("");
            setImportProgress(null);
            setImportChecked(false);
          }}
          onCheck={() => {
            setImportChecked(true);
            setImportStatus(
              `檢查完成：${importStats.valid} 筆可匯入${importStats.errors ? `，${importStats.errors} 筆需要修正` : ""}。`,
            );
          }}
          onConfirm={confirmImport}
        />
      )}

      {activeTab === "contacts" && !importOpen && (
        <section
          style={styles.card}
        >
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>常用聯絡人</h2>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ImportFileButton onChange={handleImportFile} />
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={dedupeLoading}
                onClick={() => removeDuplicateDirectoryItems("contacts")}
              >
                {dedupeLoading ? "檢查中…" : "刪除重複資訊"}
              </button>
              <span style={styles.status}>
                {contacts.length
                  ? `${contacts.length} 筆已載入`
                  : "向下滾動載入"}
              </span>
            </div>
          </div>
          <div style={styles.content}>
            <form style={styles.form} onSubmit={saveContact}>
              <Field label="聯絡人姓名 *">
                <input
                  style={styles.input}
                  value={contactForm.name}
                  onChange={(event) =>
                    setContactForm({ ...contactForm, name: event.target.value })
                  }
                  placeholder="例如：王小明"
                />
              </Field>
              <Field label="公司或店家名稱">
                <input
                  style={styles.input}
                  value={contactForm.company}
                  onChange={(event) =>
                    setContactForm({
                      ...contactForm,
                      company: event.target.value,
                    })
                  }
                  placeholder="選填"
                />
              </Field>
              <Field label="電話">
                <input
                  style={styles.input}
                  value={contactForm.phone}
                  onChange={(event) =>
                    setContactForm({
                      ...contactForm,
                      phone: event.target.value,
                    })
                  }
                  placeholder="選填，例如：0912345678"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  style={styles.input}
                  value={contactForm.email}
                  onChange={(event) =>
                    setContactForm({
                      ...contactForm,
                      email: event.target.value,
                    })
                  }
                  placeholder="選填，例如：name@example.com"
                />
              </Field>
              <Field label="地址 *" full>
                <input
                  style={styles.input}
                  value={contactForm.address}
                  onChange={(event) =>
                    setContactForm({
                      ...contactForm,
                      address: event.target.value,
                    })
                  }
                  placeholder="例如：高雄市三民區建國三路123號4樓"
                />
              </Field>
              <div style={styles.formActions}>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={resetForms}
                >
                  清除
                </button>
                <button
                  type="submit"
                  style={styles.primaryButton}
                  disabled={saving}
                >
                  {saving ? "查詢中…" : "新增聯絡人"}
                </button>
              </div>
            </form>
            {loading ? (
              <p style={styles.muted}>正在讀取常用聯絡人…</p>
            ) : contacts.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <p style={styles.muted}>尚未載入常用聯絡人。</p>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => loadDirectoryPage("contacts", 1)}
                >
                  載入常用聯絡人
                </button>
              </div>
            ) : (
              <div
                style={styles.tableWrap}
              >
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>姓名／公司</th>
                      <th style={styles.th}>電話</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>地址／郵遞區號</th>
                      <th style={styles.th}>操作</th>
                    </tr>
                  </thead>
                  <tbody>{contactRows}</tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === "addresses" && !importOpen && (
        <section
          style={styles.card}
        >
          <div style={styles.cardHeader}>
            <div>
            <h2 style={styles.cardTitle}>常用地址</h2>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ImportFileButton onChange={handleImportFile} />
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={dedupeLoading}
                onClick={() => removeDuplicateDirectoryItems("addresses")}
              >
                {dedupeLoading ? "檢查中…" : "刪除重複資訊"}
              </button>
              <span style={styles.status}>
                {addresses.length
                  ? `${addresses.length} 筆已載入`
                  : "向下滾動載入"}
              </span>
            </div>
          </div>
          <div style={styles.content}>
            <form style={styles.form} onSubmit={saveAddress}>
              <Field label="地址 *" full>
                <input
                  style={styles.input}
                  value={addressForm.address}
                  onChange={(event) =>
                    setAddressForm({ address: event.target.value })
                  }
                  placeholder="例如：高雄市三民區建國三路123號4樓"
                />
              </Field>
              <div style={styles.formActions}>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={resetForms}
                >
                  清除
                </button>
                <button
                  type="submit"
                  style={styles.primaryButton}
                  disabled={saving}
                >
                  {saving ? "查詢中…" : "新增地址"}
                </button>
              </div>
            </form>
            {loading ? (
              <p style={styles.muted}>正在讀取常用地址…</p>
            ) : addresses.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <p style={styles.muted}>尚未載入常用地址。</p>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => loadDirectoryPage("addresses", 1)}
                >
                  載入常用地址
                </button>
              </div>
            ) : (
              <div
                style={styles.tableWrap}
              >
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>地址／郵遞區號</th>
                      <th style={styles.th}>座標</th>
                      <th style={styles.th}>定位狀態</th>
                      <th style={styles.th}>操作</th>
                    </tr>
                  </thead>
                  <tbody>{addressRows}</tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      <EditModal
        target={editTarget}
        contactForm={contactForm}
        setContactForm={setContactForm}
        addressForm={addressForm}
        setAddressForm={setAddressForm}
        onClose={() => setEditTarget(null)}
        onSubmit={saveEdit}
      />
    </div>
  );
}

function targetAddress(value) {
  return clean(value);
}

export { mapImportedRows, parseCsv };
